import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import MethodExecutionLimitError from 'studies/typed/shared/MethodExecutionLimitError';

import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {ExperimentFlowchart, ExperimentMethod} from './ExperimentalInputs';
import ProcedureRunPlan, {ExperimentalInputsContext} from './ProcedureRunPlan';
import {ReplicateTable} from './ReplicateTable';
import {Condition} from './useExperimentDesignState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

export function ProcedureRunDesignEditor(props: {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  procedureRunInputs: ExperimentalInputs | null;
  setProcedureRunInputs: (procedureRunInputs: ExperimentalInputs) => void;
  context: ExperimentalInputsContextProperties;
  selectedMethodId: string;
  setSelectedMethodId: (selectedMethodId: string) => void;
  procedureMethods: Array<ExperimentMethod>;
  procedureFlowchart: ExperimentFlowchart;
  methodExecutionLimitState: MethodExecutionLimitState;
  estimate: TimingEstimateResult;
  procedureType: ProcedureType;
}): JSX.Element {
  const {isOverLimit, replicateLimit} = props.methodExecutionLimitState;

  const editNumColsButtons = (
    <EditNumConditionsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
    />
  );

  return (
    <>
      <div className={css(styles.container)}>
        <div className={css(styles.header)}>
          <ReplicateTable
            conditions={props.conditions}
            setConditions={props.setConditions}
            selectedMethodId={props.selectedMethodId}
            experimentalInputs={props.procedureRunInputs}
            setExperimentalInputs={props.setProcedureRunInputs}
            editNumColsButtons={editNumColsButtons}
            locked={props.context.locked}
            estimate={props.estimate}
            procedureType={props.procedureType}
            dataTestAttrs={{component: 'ReplicateTable', element: 'replicateTable'}}
          />
          {isOverLimit && (
            <div className={css(styles.banner)}>
              <MethodExecutionLimitError
                replicateLimit={replicateLimit}
                procedureType={props.procedureType}
              />
            </div>
          )}
        </div>
        <div className={css(styles.body)}>
          <ExperimentalInputsContext.Provider value={props.context}>
            <ProcedureRunPlan
              methods={props.procedureMethods}
              procedureFlowchart={props.procedureFlowchart}
              selectedMethodId={props.selectedMethodId}
              onChangeSelectedMethod={props.setSelectedMethodId}
              procedureType={props.procedureType}
            />
          </ExperimentalInputsContext.Provider>
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  container: {display: 'flex', flexDirection: 'column', height: '100%'},
  header: {
    flex: 0,
    borderBottom: '1px solid #e0e0e0',
    paddingLeft: 10,
    // paddingRight: 10 is already handled by .mediocre-tableEditable-titleBarWrapper
  },
  body: {flex: 1, position: 'relative', minHeight: 0},
  banner: {paddingBottom: '9px', display: 'inline-block'},
});
